import OperationButton, {
  followType,
} from "@/components/FollowButton/operationButton";
import { getMuseName, isCertifyCompany } from "@/constants/areas";
import useAppStore from "@/context/store";
import { followBoard, likeBoard, unfollowBoard, unlikeBoard } from "@/services";
import getTimeDuration from "@/utils/getTimeDuration";
import useRequest from "@/utils/useReq";
import useSaveListCache from "@/utils/useSaveListCache";
import { Button } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Case, Else, If, Switch, Then, When } from "react-if";
import displayNum from "../../utils/displayNum";
import imageUrl from "../../utils/imageUrl";
import BoardModal from "../BoardModal";
import HBIcon from "../HBIcon";
import Image from "../Image";
import { useLogin } from "../LoginModal";
import Logo from "./Logo";
import styles from "./index.module.less";
const PinPreview = dynamic(import("../PinPreview"), {
  ssr: false,
});

export default function BoardCard({
  className,
  record,
  hideButton,
  hideUser,
  style,
  noClick,
  type = "default",
  disableHoverMask,
  onDelete,
  draggable,
  onFollow,
  domRef,
  isDragging,
  hideTime,
  ...rest
}) {
  const { config, user } = useAppStore(({ config, user }) => ({
    config,
    user,
  }));

  const [board, setBoard] = useState(record);
  // 获取卡片上榜信息
  useSaveListCache();
  const { listCacheMap } = useAppStore((state) => {
    return {
      listCacheMap: state.listCacheMap,
    };
  });
  const { runAsync: runFollow } = useRequest(followBoard, {
    manual: true,
  });
  const { runAsync: runUnfollow } = useRequest(unfollowBoard, {
    manual: true,
  });

  const { runAsync: runLike } = useRequest(likeBoard, {
    manual: true,
  });
  const { runAsync: runUnlike } = useRequest(unlikeBoard, {
    manual: true,
  });

  const { login } = useLogin();

  useEffect(() => {
    setBoard(record);
  }, [record]);

  const imgs = useMemo(() => {
    const list = [];
    if (board.cover?.file) {
      list.push(board.cover.file);
    }

    board.pins?.slice(0, 4).forEach((pin) => {
      list.push({ ...pin.file, raw_text: pin?.raw_text });
    });

    return list;
  }, [board]);

  const onToggleLike = useCallback(async () => {
    if (!user) {
      const result = await login();
      if (!result) {
        return;
      }
    }

    if (board?.liked) {
      const result = await runUnlike(board.board_id);
      if (!result.err) {
        setBoard({
          ...board,
          liked: false,
          like_count: board?.like_count - 1,
        });
      }
    } else {
      const result = await runLike(board.board_id);
      if (!result.err) {
        setBoard({
          ...board,
          liked: true,
          like_count: board?.like_count + 1,
        });
      }
    }
  }, [board, login, runLike, runUnlike, user]);
  const onToggleFollow = useCallback(async () => {
    if (!user) {
      const result = await login();
      if (!result) {
        return;
      }
    }

    if (board?.following) {
      const result = await runUnfollow(board.board_id);
      if (!result.err) {
        const newBoard = {
          ...board,
          following: false,
          follow_count: board?.follow_count - 1,
        };
        setBoard(newBoard);
        onFollow && onFollow(false, newBoard);
      }
    } else {
      const result = await runFollow(board.board_id);
      if (!result.err) {
        const newBoard = {
          ...board,
          following: true,
          follow_count: board?.follow_count + 1,
        };
        setBoard(newBoard);
        onFollow && onFollow(true, newBoard);
      }
    }
  }, [board, login, runFollow, runUnfollow, onFollow, user]);

  const onEdit = useCallback(() => {
    BoardModal.open({
      name: "boardEditModal",
      boardId: board.board_id,
      onUpdate(board) {
        setBoard(board);
      },
      onDelete: () => {
        onDelete && onDelete(board.board_id);
      },
    });
  }, [board, onDelete]);

  // 奇葩的待归类画板
  const isSpecial = useMemo(() => {
    return board?.is_private === 2;
  }, [board]);

  // 0：公开
  // 1：管理员设为私密，前端当做0处理
  // 2：待分类
  // 3：私密
  const isPrivate = useMemo(() => {
    return board?.is_private !== 0 && board?.is_private !== 1;
  }, [board]);

  const isUserOffical = useMemo(() => {
    return config?.officialUsers?.split(",").includes(board?.user?.urlname);
  }, [config, board]);

  const time = useMemo(() => {
    const createTime = record?.pins?.[0]?.created_at;
    if (createTime) {
      let diffMonth = dayjs(dayjs().format("YYYY-MM-DD")).diff(
        createTime * 1000,
        "month",
        true
      );
      // 超过三个月的不展示
      if (diffMonth.toFixed() > 3) {
        return false;
      }
      return getTimeDuration(createTime * 1000);
    }
    return false;
  }, [record]);

  const onImgLoad = useCallback((evt) => {
    const target = evt.target;
    target?.classList.add("transparent-img-bg");
  }, []);

  // 列表类型
  if (type === "list") {
    return (
      <div
        className={classNames(styles.small, {
          [styles.isDragging]: isDragging,
        })}
        {...rest}
        style={style}
        ref={domRef}
      >
        <Link href={`/boards/${board.board_id}`}>
          <a className={styles.listBoardType}>
            <div className={styles.wrap}>
              {imgs.length ? (
                <Image
                  onLoad={onImgLoad}
                  width={80}
                  height={80}
                  src={imageUrl(imgs[0], {
                    width: 80,
                    rect: "sq",
                  })}
                  showError={true}
                  alt="画板封面"
                />
              ) : (
                <div className={styles.block}></div>
              )}
              <div className={styles.info}>
                <p className={styles.name} title={board.title}>
                  {board.title}
                </p>
                <p>{displayNum(board.pin_count)}采集</p>
              </div>
            </div>
            {isPrivate ||
              (board.extra?.is_creation && (
                <div className={styles.tag}>
                  {board.extra?.is_creation && "原创"}
                  {isPrivate && (
                    <HBIcon className={styles.lock} type="ic_lock" />
                  )}
                </div>
              ))}

            {draggable && (
              <div title="拖拽排序" className={styles.dragAnchor}>
                <HBIcon type="drag_indicator" />
              </div>
            )}
          </a>
        </Link>
      </div>
    );
  }
  return (
    <div
      {...rest}
      className={classNames(styles.card, className, {
        [styles.disabledHoverMask]: disableHoverMask,
        [styles.isDragging]: isDragging,
      })}
      style={style}
      ref={domRef}
      data-dist-code={record?.custom_recommend ? "人工" : "算法"}
    >
      <div className={styles.album}>
        <When condition={!isSpecial && !hideButton && type === "default"}>
          <If condition={user?.user_id === board.user_id}>
            <Then>
              <div className={styles.top}>
                <div className={styles.left}></div>
                <div className={styles.right}>
                  <Button
                    className={classNames([
                      styles.edit,
                      draggable ? styles.showDragAnchor : null,
                    ])}
                    onClick={onEdit}
                    data-gd-click="button_click"
                    data-button-name="编辑"
                    data-board-id={board?.board_id}
                  >
                    <HBIcon className={styles.icon} type="ic_edit" />
                    {draggable && (
                      <div className={styles.dragAnchor} title="拖拽排序">
                        <HBIcon type="drag_indicator" />
                      </div>
                    )}
                  </Button>
                </div>
              </div>
            </Then>
            <Else>
              <div className={styles.top}>
                <div className={styles.left}>
                  <If condition={board?.following}>
                    <Then>
                      {board?.user_following === followType.unIndependent ? (
                        <Button
                          className={styles.focus}
                          type="text"
                          onClick={onToggleFollow}
                          data-gd-click="button_click"
                          data-button-name="取消关注"
                          data-board-id={board?.board_id}
                        >
                          已关注
                        </Button>
                      ) : (
                        <div className={styles.location}>
                          <OperationButton
                            // visible
                            className={styles.operationButton}
                            onClick={onToggleFollow}
                            data-board-id={board?.board_id}
                            data-gd-click="button_click"
                            data-button-name="取消关注"
                            data-concern-object-type="画板"
                            getPopupContainer={(triggerNode) =>
                              triggerNode.parentNode.parentNode
                            }
                            data-concern-object-name={board?.title}
                            boardInfo={board}
                          ></OperationButton>
                        </div>
                      )}
                    </Then>
                    <Else>
                      <Button
                        className={styles.focus}
                        type="primary"
                        icon={<HBIcon type="ic_star" />}
                        onClick={onToggleFollow}
                        data-board-id={board?.board_id}
                        data-gd-click="button_click"
                        data-button-name="关注"
                      >
                        关注
                      </Button>
                    </Else>
                  </If>
                </div>
                <div className={styles.right}>
                  <span
                    data-tooltip={board?.liked ? "取消喜欢" : "喜欢"}
                    data-tooltip-position="bottom"
                  >
                    <Button
                      className={classNames([
                        styles.favour,
                        board?.liked ? styles.active : null,
                      ])}
                      data-gd-click="button_click"
                      data-board-id={board.board_id}
                      data-button-name={board?.liked ? "取消喜欢" : "喜欢"}
                      onClick={onToggleLike}
                    >
                      <HBIcon
                        type={board?.liked ? "ic_favorite" : "favorite"}
                      />
                    </Button>
                  </span>
                </div>
              </div>
            </Else>
          </If>
        </When>

        <When condition={!isSpecial}>
          <Switch>
            <Case condition={board.extra?.is_creation && isPrivate}>
              <div className={styles.label}>
                <span className={styles.mr}>原创</span>
                <HBIcon type="ic_lock" />
              </div>
            </Case>
            <Case condition={board.extra?.is_creation}>
              <div className={styles.label}>原创</div>
            </Case>
            <Case condition={isPrivate}>
              <div className={styles.label}>
                <HBIcon type="ic_lock" />
              </div>
            </Case>
          </Switch>
        </When>

        <When condition={time && !hideTime}>
          <div className={styles.time}>
            <div className={styles.dot}></div>
            <span>{time}更新</span>
          </div>
        </When>
        {/* 推荐理由 */}
        <When condition={hideTime && record.rec_reason}>
          <div className={styles.reason}>
            <HBIcon type={"ic_hot"} />
            {record.rec_reason}
          </div>
        </When>

        {/* 图片 */}
        <a
          href={`/boards/${board.board_id}`}
          target="_blank"
          rel="noreferrer"
          className={styles.imgWrapper}
          data-gd-expose="board_expose"
          data-gd-click="board_click"
          data-board-id={board.board_id}
          {...(noClick
            ? {
                onClick(e) {
                  e.preventDefault();
                },
              }
            : null)}
        >
          {[styles.first, styles.second, styles.third, styles.fourth].map(
            (style, index) => (
              <If condition={imgs[index]} key={index}>
                <Then>
                  <div className={classNames([styles.img, style])}>
                    <Image
                      onLoad={onImgLoad}
                      src={imageUrl(imgs[index], {
                        width: index === 0 ? 490 : 180,
                        rect: "sq",
                      })}
                      loading="lazy"
                      alt={imgs[index]?.raw_text || "采集图片"}
                      showError={true}
                    />
                  </div>
                </Then>
                <Else>
                  <div
                    className={classNames([styles.img, styles.bg, style])}
                    key={index}
                  ></div>
                </Else>
              </If>
            )
          )}
        </a>
      </div>

      <div className={styles.header}>
        <a
          className={styles.title}
          href={`/boards/${board.board_id}`}
          target="_blank"
          title={board.title}
          rel="noreferrer"
          {...(noClick
            ? {
                onClick(e) {
                  e.preventDefault();
                },
              }
            : null)}
        >
          {board.title}
        </a>
        <PinPreview className={styles.preview} board={board} />
      </div>

      <div className={styles.info}>
        {!!listCacheMap && !!board && !!listCacheMap[board.board_id] ? (
          <Logo listInfo={listCacheMap[board.board_id]}></Logo>
        ) : null}
        {displayNum(board.pin_count)}采集 · {displayNum(board.follow_count)}
        人关注
      </div>

      <When condition={!hideUser}>
        <a
          className={styles.user}
          href={`/user/${board.user?.urlname}`}
          target="_blank"
          rel="noreferrer"
          data-gd-click="button_click"
          data-button-name="去个人页"
          data-board-id={board.board_id}
          {...(noClick
            ? {
                onClick(e) {
                  e.preventDefault();
                },
              }
            : null)}
          title={board.user?.username}
        >
          <div className={styles.avatar}>
            <img
              alt={board.user?.username || "查看用户"}
              src={imageUrl(board.user?.avatar, { width: 75, rect: "sq" })}
              loading="lazy"
            />
          </div>
          <div className={styles.name}>{board.user?.username}</div>
          <Switch>
            <Case condition={isUserOffical}>
              <div className={styles.offical} title="花瓣官方"></div>
            </Case>
            <Case condition={board.user?.extra?.is_museuser}>
              <div
                className={classNames(styles.icon, [
                  isCertifyCompany(board?.user?.museUser?.category[0])
                    ? styles.company
                    : styles.personal,
                ])}
                title={
                  board?.user?.museUser?.category[0]
                    ? `认证${getMuseName(board.user.museUser.category[0])}`
                    : "认证设计师"
                }
              >
                <HBIcon type="register" />
              </div>
            </Case>
          </Switch>
        </a>
      </When>

      <When condition={!hideButton && type === "primary"}>
        <div className={styles.button}>
          <If condition={board?.following}>
            <Then>
              {board?.user_following === followType.unIndependent ? (
                <Button
                  className={styles.focus}
                  onClick={onToggleFollow}
                  data-board-id={board?.board_id}
                  data-gd-click="button_click"
                  data-button-name="取消关注"
                  data-concern-object-type="画板"
                  data-concern-object-name={board?.title}
                >
                  已关注
                </Button>
              ) : (
                <OperationButton
                  onClick={onToggleFollow}
                  data-board-id={board?.board_id}
                  data-gd-click="button_click"
                  data-button-name="取消关注"
                  data-concern-object-type="画板"
                  data-concern-object-name={board?.title}
                  boardInfo={board}
                ></OperationButton>
              )}
              {/* <Button
                className={styles.focus}
                onClick={onToggleFollow}
                data-board-id={board?.board_id}
                data-gd-click="button_click"
                data-button-name="取消关注"
                data-concern-object-type="画板"
                data-concern-object-name={board?.title}
              >
                已关注
              </Button> */}
            </Then>
            <Else>
              <Button
                className={styles.focus}
                type="primary"
                icon={<HBIcon type="ic_star" />}
                onClick={onToggleFollow}
                data-board-id={board?.board_id}
                data-gd-click="button_click"
                data-button-name="关注"
                data-concern-object-type="画板"
                data-concern-object-name={board?.title}
              >
                关注
              </Button>
            </Else>
          </If>
        </div>
      </When>
    </div>
  );
}
